import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { SERVER_URL } from "../services/api";
import { aplicarCorPrimaria } from "../lib/cores";
import { aplicarPreferenciasSalvas } from "../lib/preferencias";
import seloJuridico from "../public/selo-juridico.png";

const AuthContext = createContext(null);

const lerJSON = (chave) => {
    try {
        return JSON.parse(localStorage.getItem(chave));
    } catch {
        return null;
    }
};

export const AuthProvider = ({ children }) => {
    const [usuario, setUsuario] = useState(() => lerJSON("usuario"));
    const [admin, setAdmin] = useState(() => lerJSON("admin"));

    const guardarSessao = (dados, tipo) => {
        localStorage.setItem("token", dados.token);
        if (tipo === "admin") {
            localStorage.setItem("admin", JSON.stringify(dados.admin));
            localStorage.removeItem("usuario");
            setUsuario(null);
            setAdmin(dados.admin);
        } else {
            localStorage.setItem("usuario", JSON.stringify(dados.usuario));
            localStorage.removeItem("admin");
            setAdmin(null);
            setUsuario(dados.usuario);
        }
    };

    const login = async (email, senha) => {
        const { data } = await api.post("/auth/login", { email, senha });
        guardarSessao(data, "usuario");
        return data.usuario;
    };

    const loginAdmin = async (login, senha) => {
        const { data } = await api.post("/admin/login", { login, senha });
        guardarSessao(data, "admin");
        return data.admin;
    };

    const registrar = async (dados) => {
        const { data } = await api.post("/auth/registrar", dados);
        guardarSessao(data, "usuario");
        return data.usuario;
    };

    const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("usuario");
        localStorage.removeItem("admin");
        setUsuario(null);
        setAdmin(null);
        aplicarCorPrimaria(null);
    };

    /** Relê os dados do escritório (após trocar cor ou logo em Configurações) */
    const recarregarEscritorio = useCallback(async () => {
        if (!localStorage.getItem("usuario")) return;
        try {
            const { data } = await api.get("/escritorio");
            setUsuario((atual) => {
                if (!atual) return atual;
                const atualizado = {
                    ...atual,
                    nome_escritorio: data.nome,
                    logo_url: data.logo_url,
                    cor_primaria: data.cor_primaria
                };
                localStorage.setItem("usuario", JSON.stringify(atualizado));
                return atualizado;
            });
        } catch {
            /* sessão expirada: o interceptor do axios já redireciona */
        }
    }, []);

    // Preferências da pessoa (tema/tamanho) + cor do escritório
    useEffect(() => {
        aplicarPreferenciasSalvas();
    }, []);

    useEffect(() => {
        aplicarCorPrimaria(usuario?.cor_primaria);
    }, [usuario]);

    const autenticado = !!usuario && !!localStorage.getItem("token");
    const adminAutenticado = !!admin && !!localStorage.getItem("token");
    // Estagiário só edita agenda e prazos; o resto é leitura
    const podeEditar = autenticado && usuario.papel !== "estagiario";
    const ehProprietario = autenticado && usuario.papel === "proprietario";

    // Logo enviada pelo escritório; sem ela, o selo padrão do sistema.
    // URL completa (Cloudinary) usa direto; caminho relativo (/uploads) recebe
    // o prefixo do servidor.
    const logoEscritorio = usuario?.logo_url
        ? (/^https?:\/\//.test(usuario.logo_url) ? usuario.logo_url : `${SERVER_URL}${usuario.logo_url}`)
        : seloJuridico;

    return (
        <AuthContext.Provider value={{
            usuario, admin, autenticado, adminAutenticado,
            podeEditar, ehProprietario, logoEscritorio,
            login, loginAdmin, registrar, logout, recarregarEscritorio
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
